import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getCouponDetail, deleteCoupon, toggleCouponStatus, getProducts, getCategories } from '../config/api';

const CouponDetail = () => {
  const { couponId } = useParams();
  const navigate = useNavigate();
  const [coupon, setCoupon] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadCouponDetail();
    loadProducts();
    loadCategories();
  }, [couponId]);

  const loadCouponDetail = async () => {
    try {
      const response = await getCouponDetail(couponId);
      const couponData = response.data || response;
      setCoupon(couponData);
    } catch (error) {
      console.error('Error loading coupon detail:', error);
      alert('Failed to load coupon details');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    try {
      const productListRes = await getProducts();
      setProducts(productListRes.data || []);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const categoryListRes = await getCategories();
      setCategories(categoryListRes.data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleToggleStatus = async () => {
    try {
      setUpdating(true);
      await toggleCouponStatus(couponId, !coupon.active);
      setCoupon({ ...coupon, active: !coupon.active });
      alert(`Coupon ${!coupon.active ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error toggling coupon status:', error);
      alert('Failed to update coupon status');
    } finally {
      setUpdating(false);
    }
  };

  const formatValue = (type, value) => {
    return type === 'FIXED' ? `₹${parseFloat(value).toFixed(2)}` : `${value}%`;
  };

  const isExpired = (validTill) => {
    return new Date(validTill) < new Date();
  };

  const getApplicableProducts = () => {
    if (!coupon.applicable_product_ids || coupon.applicable_product_ids.length === 0) {
      return 'All Products';
    }
    
    const applicableProducts = products.filter(product => 
      coupon.applicable_product_ids.includes(product.id)
    );
    
    return applicableProducts.map(product => `${product.skuCode} - ${product.name}`).join(', ');
  };

  const getApplicableCategories = () => {
    if (!coupon.applicable_category_ids || coupon.applicable_category_ids.length === 0) {
      return 'All Categories';
    }
    
    const applicableCategories = categories.filter(category => 
      coupon.applicable_category_ids.includes(category.id)
    );
    
    return applicableCategories.map(category => category.name).join(', ');
  };

  if (loading) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '60px', color: '#6c757d' }}>
          Loading coupon details...
        </div>
      </div>
    );
  }

  if (!coupon) {
    return (
      <div className="container">
        <div style={{ textAlign: 'center', padding: '60px', color: '#dc3545' }}>
          Coupon not found
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div className="order-detail-header">
          <div className="order-detail-title">
            <div className="breadcrumb">
              <Link to="/coupons">Coupons</Link> / {coupon.code}
            </div>
            <h1>Coupon Details</h1>
          </div>
          
          <div className="order-actions">
            <button
              onClick={handleToggleStatus}
              className={`btn ${coupon.active ? 'btn-warning' : 'btn-success'}`}
              disabled={updating}
            >
              {updating ? 'Updating...' : (coupon.active ? 'Deactivate' : 'Activate')}
            </button>
          </div>
        </div>

        {/* Coupon Information */}
        <div className="card">
          <h3 className="section-title">Coupon Information</h3>
          <div className="order-info">
            <div className="info-item">
              <label className="info-label">Coupon Code</label>
              <div className="info-value" style={{ 
                fontFamily: 'monospace', 
                fontSize: '18px', 
                fontWeight: '600',
                color: '#0d6efd'
              }}>
                {coupon.code}
              </div>
            </div>
            
            <div className="info-item">
              <label className="info-label">Type</label>
              <div className="info-value">
                <span className={`status-badge ${coupon.type === 'FIXED' ? 'status-shipped' : 'status-delivered'}`}>
                  {coupon.type === 'FIXED' ? 'Fixed Amount' : 'Percentage'}
                </span>
              </div>
            </div>
            
            <div className="info-item">
              <label className="info-label">Discount Value</label>
              <div className="info-value" style={{ fontSize: '20px', color: '#28a745', fontWeight: '600' }}>
                {formatValue(coupon.type, coupon.value)}
              </div>
            </div>
            
            <div className="info-item">
              <label className="info-label">Status</label>
              <div className="info-value">
                <span className={`status-badge ${coupon.active ? 'status-confirmed' : 'status-pending'}`}>
                  {coupon.active ? 'Active' : 'Inactive'}
                </span>
                {isExpired(coupon.valid_till) && (
                  <span className="status-badge" style={{ backgroundColor: '#dc3545', marginLeft: '10px' }}>
                    Expired
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Conditions & Limits */}
        <div className="card">
          <h3 className="section-title">Conditions & Limits</h3>
          <div className="order-info">
            <div className="info-item">
              <label className="info-label">Minimum Cart Value</label>
              <div className="info-value">
                {coupon.min_cart_value ? `₹${parseFloat(coupon.min_cart_value).toFixed(2)}` : 'No minimum'}
              </div>
            </div>
            
            <div className="info-item">
              <label className="info-label">Maximum Discount</label>
              <div className="info-value">
                {coupon.max_discount ? `₹${parseFloat(coupon.max_discount).toFixed(2)}` : 'No limit'}
              </div>
            </div>
            
            <div className="info-item">
              <label className="info-label">Usage Limit</label>
              <div className="info-value">
                {coupon.usage_limit || 'Unlimited'}
              </div>
            </div>
            
            <div className="info-item">
              <label className="info-label">Valid for Initial Orders</label>
              <div className="info-value">
                {coupon.valid_for_initial ? `First ${coupon.valid_for_initial} orders` : 'All orders'}
              </div>
            </div>
            
            <div className="info-item">
              <label className="info-label">Times Used</label>
              <div className="info-value" style={{ color: '#6c757d' }}>
                {coupon.used_count || 0} times
              </div>
            </div>
          </div>
        </div>

        {/* Validity Period */}
        <div className="card">
          <h3 className="section-title">Validity Period</h3>
          <div className="order-info">
            <div className="info-item">
              <label className="info-label">Valid From</label>
              <div className="info-value">
                {new Date(coupon.valid_from).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>
            
            <div className="info-item">
              <label className="info-label">Valid Till</label>
              <div className="info-value" style={{ 
                color: isExpired(coupon.valid_till) ? '#dc3545' : '#212529',
                fontWeight: isExpired(coupon.valid_till) ? '600' : 'normal'
              }}>
                {new Date(coupon.valid_till).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                {isExpired(coupon.valid_till) && ' (Expired)'}
              </div>
            </div>
          </div>
        </div>

        {/* Applicable Products */}
        <div className="card">
          <h3 className="section-title">Applicable Products</h3>
          <div className="info-value" style={{ 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '6px',
            lineHeight: '1.6'
          }}>
            {getApplicableProducts()}
          </div>
        </div>

        {/* Applicable Categories */}
        <div className="card">
          <h3 className="section-title">Applicable Categories</h3>
          <div className="info-value" style={{ 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '6px',
            lineHeight: '1.6'
          }}>
            {getApplicableCategories()}
          </div>
        </div>

        {/* B2B Customer Targeting */}
        <div className="card">
          <h3 className="section-title">B2B Customer Targeting</h3>
          <div className="info-value" style={{ 
            padding: '15px', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '6px',
            lineHeight: '1.6'
          }}>
            {coupon.applicable_gst_nums && coupon.applicable_gst_nums.length > 0 
              ? coupon.applicable_gst_nums[0]
              : 'All Customers'
            }
          </div>
        </div>

        {/* Audience Restriction */}
        <div className="card">
          <h3 className="section-title">Audience Restriction</h3>
          <div className="info-value" style={{ 
            padding: '15px', 
            backgroundColor: coupon.audience_id ? '#eff6ff' : '#f8f9fa', 
            borderRadius: '6px',
            lineHeight: '1.6',
            border: coupon.audience_id ? '1px solid #bfdbfe' : 'none',
          }}>
            {coupon.audience_id ? (
              <span style={{ fontWeight: '600', color: '#1d4ed8' }}>
                Restricted to AUD-{coupon.audience_id}
                {coupon.audience_name && ` — ${coupon.audience_name}`}
              </span>
            ) : (
              'Public Coupon (no audience restriction)'
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponDetail;